import React, { useCallback, useEffect, useState } from 'react';
import { HStack, SimpleGrid, useToast } from '@chakra-ui/react';
import { MdFileDownload, MdHistory, MdPeopleAlt, MdTune } from 'react-icons/md';
import { listStaff, getStaff } from '../../../repositories/staff.repository';
import { listActivityLogs } from '../../../repositories/activityLog.repository';
import { getSystemConfig } from '../../../repositories/systemConfig.repository';
import {
  getVillageSummary,
  getBuSiteSummary,
  getDashboardCounts,
  exportVillageSummaryExcel,
  exportBuSiteSummaryExcel,
} from '../../../repositories/dashboard.repository';
import { StaffFormModal } from '../staff/StaffFormModal';
import { StaffPasswordModal } from '../staff/StaffPasswordModal';
import SystemConfigPage from '../system-config';
import {
  HubAmberButton,
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubLoading,
  HubMetricCard,
  HubPanel,
  HubRow,
  HubTable,
  useHubHeaderActions,
} from './hubUi';

function isNavratriConfigKey(key) {
  return String(key || '')
    .toLowerCase()
    .startsWith('navratri');
}

export function HubStaffHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [configCount, setConfigCount] = useState(null);
  const [activityTotal, setActivityTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [counts, config, activities] = await Promise.all([
        getDashboardCounts(),
        getSystemConfig().catch(() => null),
        listActivityLogs({ page: 1, pageSize: 1 }).catch(() => null),
      ]);
      setData(counts);
      if (Array.isArray(config)) {
        setConfigCount(config.filter((r) => !isNavratriConfigKey(r.key || r.id)).length);
      } else if (config && typeof config === 'object') {
        setConfigCount(
          Object.keys(config).filter((k) => !isNavratriConfigKey(k)).length,
        );
      } else {
        setConfigCount(null);
      }
      setActivityTotal(activities?.total != null ? Number(activities.total) : null);
    } catch (e) {
      setError(e.message || 'Failed to load staff counts');
      toast({ title: e.message || 'Failed to load staff counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const staff = data?.staff || {};
  const inactive = Math.max(0, (staff.total ?? 0) - (staff.active ?? 0));

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <HubMetricCard
          icon={MdPeopleAlt}
          label="Staff directory"
          total={String(staff.total ?? 0)}
          metrics={[
            { label: 'Active', value: String(staff.active ?? 0) },
            { label: 'Inactive', value: String(inactive) },
          ]}
          onClick={() => push({ id: 'staffList', label: 'Directory' })}
        />
        <HubCard
          icon={MdHistory}
          label="System logs"
          value={activityTotal != null ? String(activityTotal) : '—'}
          hint="Who did what"
          onClick={() => push({ id: 'activities', label: 'System logs' })}
        />
        <HubCard
          icon={MdTune}
          label="System config"
          value={configCount != null ? String(configCount) : '—'}
          hint="Live config keys"
          onClick={() => push({ id: 'systemConfig', label: 'System config' })}
        />
      </SimpleGrid>
    </>
  );
}

export function HubStaffList({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [activeStaff, setActiveStaff] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStaff({
        pageSize: 100,
        search: search.trim() || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load staff', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search staff…"
        onClear={() => setSearch('')}
        extra={
          <>
            <HubAmberButton
              onClick={() => {
                setActiveStaff(null);
                setFormOpen(true);
              }}
            >
              Add staff
            </HubAmberButton>
            <HubGhostButton onClick={() => push({ id: 'activities', label: 'System logs' })}>
              System logs →
            </HubGhostButton>
          </>
        }
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({
            id: 'staffDetail',
            label: r.fullName || r.name || r.email,
            itemId: r.id,
          })
        }
        columns={[
          {
            key: 'name',
            label: 'Name',
            render: (r) => r.fullName || r.name || '—',
          },
          {
            key: 'roles',
            label: 'Roles',
            render: (r) =>
              Array.isArray(r.roles)
                ? r.roles.map((x) => x.name || x).join(', ')
                : r.roleNames || '—',
          },
          { key: 'email', label: 'Email' },
          {
            key: 'lastLogin',
            label: 'Last login',
            render: (r) => r.lastLoginAt || r.lastLogin || '—',
          },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <HubGhostButton
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveStaff(r);
                  setFormOpen(true);
                }}
              >
                Edit
              </HubGhostButton>
            ),
          },
        ]}
      />

      <StaffFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setActiveStaff(null);
        }}
        staff={activeStaff}
        onSaved={load}
      />
    </>
  );
}

export function HubStaffDetail({ push, itemId }) {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const row = await getStaff(itemId);
      if (!row) throw new Error('Staff not found');
      setStaff(row);
    } catch (e) {
      setError(e.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const name = staff?.fullName || staff?.name || staff?.email;
  useHubHeaderActions(
    () =>
      staff ? (
        <>
          <HubAmberButton onClick={() => setFormOpen(true)}>Edit</HubAmberButton>
          <HubGhostButton onClick={() => setPasswordOpen(true)}>
            Update password
          </HubGhostButton>
          <HubGhostButton
            onClick={() =>
              push({
                id: 'activities',
                label: `${name} logs`,
                staffId: staff.id,
              })
            }
          >
            Their logs →
          </HubGhostButton>
        </>
      ) : null,
    [staff, name],
  );

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;
  if (!staff) return <HubError message="Staff not found" />;

  return (
    <>
      <HubPanel>
        <HubRow label="Email">{staff.email}</HubRow>
        <HubRow label="Mobile">{staff.mobile || staff.phone}</HubRow>
        <HubRow label="Code">{staff.staffId || staff.code}</HubRow>
        <HubRow label="Active">{staff.isActive === false ? 'No' : 'Yes'}</HubRow>
        <HubRow label="Last login">{staff.lastLoginAt || staff.lastLogin}</HubRow>
        <HubRow label="Roles">
          {Array.isArray(staff.roles)
            ? staff.roles.map((x) => x.name || x).join(', ')
            : staff.roleNames || '—'}
        </HubRow>
      </HubPanel>

      <StaffFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        staff={staff}
        onSaved={load}
      />
      <StaffPasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        staff={staff}
        onSaved={load}
      />
    </>
  );
}

export function HubActivities({ staffId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listActivityLogs({
        pageSize: 100,
        search: search.trim() || undefined,
        staffId: staffId || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load activities', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search, staffId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search module, action…"
        onClear={() => setSearch('')}
      />
      <HubTable
        loading={loading}
        rows={items.map((r, i) => ({ ...r, id: r.id ?? i }))}
        columns={[
          {
            key: 'when',
            label: 'When',
            render: (r) => r.createdAt || r.when || '—',
          },
          {
            key: 'staff',
            label: 'Staff',
            render: (r) => r.staffName || r.staff?.fullName || r.staffId || '—',
          },
          { key: 'module', label: 'Module', render: (r) => r.module || '—' },
          {
            key: 'action',
            label: 'Action',
            render: (r) => r.summary || r.action || '—',
          },
        ]}
      />
    </>
  );
}

export function HubSystemConfig() {
  return <SystemConfigPage embedded />;
}

export function HubVillageReport({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVillageSummary();
        const list = Array.isArray(data) ? data : data?.items || data?.rows || [];
        setItems(
          list.map((r, i) => ({
            id: r.villageId || r.id || i,
            name: r.villageName || r.name || '—',
            members: r.memberCount ?? r.members ?? 0,
            equipment: r.equipmentCount ?? r.equipment ?? 0,
            deployed: r.deployedCount ?? r.deployed ?? 0,
          })),
        );
      } catch (e) {
        toast({ title: e.message || 'Failed to load village report', status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportVillageSummaryExcel({ includeEmpty: true });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Failed to export village report', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useHubHeaderActions(
    () => (
      <HubGhostButton
        leftIcon={<MdFileDownload />}
        onClick={handleExport}
        isLoading={exporting}
        isDisabled={loading || exporting}
      >
        Export Excel
      </HubGhostButton>
    ),
    [exporting, loading],
  );

  return (
    <>
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({ id: 'memberList', label: `${r.name} users`, filter: 'all' })
        }
        columns={[
          { key: 'name', label: 'Village' },
          { key: 'members', label: 'Users' },
          { key: 'equipment', label: 'Equipment' },
          { key: 'deployed', label: 'Deployed' },
        ]}
      />
    </>
  );
}

export function HubBuReport({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBuSiteSummary({ includeEmpty: true });
        const list = Array.isArray(data) ? data : data?.items || data?.rows || [];
        setItems(
          list.map((r, i) => ({
            id: `${r.buId || 'bu'}-${r.siteId || i}`,
            name: r.siteName || '—',
            bu: r.buName || r.buCode || '—',
            indents: r.requestTotal ?? 0,
            equipment: r.equipmentAvailable ?? 0,
          })),
        );
      } catch (e) {
        toast({ title: e.message || 'Failed to load BU report', status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportBuSiteSummaryExcel({ includeEmpty: true });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Failed to export BU / Site report', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useHubHeaderActions(
    () => (
      <HStack spacing={2}>
        <HubGhostButton
          leftIcon={<MdFileDownload />}
          onClick={handleExport}
          isLoading={exporting}
          isDisabled={loading || exporting}
        >
          Export Excel
        </HubGhostButton>
        {push ? (
          <HubAmberButton
            onClick={() =>
              push({ id: 'masterBusinessUnits', label: 'Business units' })
            }
          >
            Edit BUs & sites
          </HubAmberButton>
        ) : null}
      </HStack>
    ),
    [exporting, loading],
  );

  return (
    <>
      <HubTable
        loading={loading}
        rows={items}
        columns={[
          { key: 'bu', label: 'Business unit' },
          { key: 'name', label: 'Site' },
          { key: 'indents', label: 'Indents' },
          { key: 'equipment', label: 'Equipment' },
        ]}
      />
    </>
  );
}
